import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

export default function IntroSplash() {
  const nav = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const [muted, setMuted] = useState(false);
  const [needsTap, setNeedsTap] = useState(false); // shown if autoplay was blocked
  const audioRef = useRef(null);

  const stopAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.pause();
      a.currentTime = 0;
    } catch (err) {
      // Audio element might be in an invalid state; safe to ignore during teardown.
      if (process.env.NODE_ENV !== "production") console.debug("IntroSplash: stopAudio failed", err);
    }
  };

  const enter = () => {
    if (leaving) return;
    stopAudio();
    setLeaving(true);
    setTimeout(() => {
      try {
        sessionStorage.setItem("cs.splash.seen", "1");
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.debug("IntroSplash: sessionStorage unavailable", err);
      }
      nav("/home");
    }, 500);
  };

  // Try to autoplay audio; if blocked, arm it to start on first user interaction.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.85;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.catch((err) => {
        // Autoplay blocked by browser policy — surface a "TAP TO START" hint.
        if (process.env.NODE_ENV !== "production") console.debug("IntroSplash: autoplay blocked", err);
        setNeedsTap(true);
      });
    }
    return () => {
      try {
        a.pause();
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.debug("IntroSplash: pause on unmount failed", err);
      }
    };
  }, []);

  useEffect(() => {
    const auto = setTimeout(enter, 14000);
    const onKey = (e) => { if (e.key === "Enter" || e.key === " ") enter(); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(auto); window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const handleTap = () => {
    // If audio was blocked from autoplay, first tap starts it and DOESN'T navigate.
    if (needsTap && audioRef.current) {
      audioRef.current.play().catch(() => {});
      setNeedsTap(false);
      return;
    }
    enter();
  };

  return (
    <AnimatePresence>
      {!leaving && (
        <motion.div
          key="splash"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[100] w-full h-full bg-[#0A090D] overflow-hidden"
        >
          {/* Hidden but present audio element */}
          <audio
            ref={audioRef}
            src="/brand/intro.mp3"
            preload="auto"
            playsInline
            data-testid="splash-audio"
          />

          <button
            data-testid="intro-splash"
            onClick={handleTap}
            className="absolute inset-0 w-full h-full flex items-center justify-center cursor-pointer"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            {/* Poster image, full-bleed centered */}
            <motion.img
              src="/brand/splash.jpg"
              alt="Celebrity Smash"
              initial={{ scale: 1.15, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 w-full h-full object-cover object-center"
              draggable={false}
            />
            {/* Vignette */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: "radial-gradient(circle at 50% 40%, transparent 40%, rgba(10,9,13,0.55) 90%)",
            }} />
            {/* Scanlines */}
            <div className="absolute inset-0 pointer-events-none opacity-25" style={{
              backgroundImage: "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
              mixBlendMode: "overlay",
            }} />

            {/* Tap-to-enter prompt */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="absolute bottom-8 sm:bottom-12 left-0 right-0 flex flex-col items-center gap-2 z-10 pointer-events-none"
            >
              <motion.span
                animate={{ opacity: [0.35, 1, 0.35] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className="font-display text-lg sm:text-xl tracking-[0.4em] text-[#FDFBF7]"
                style={{ textShadow: "0 0 12px rgba(255,16,122,0.7), 0 2px 8px rgba(0,0,0,0.6)" }}
              >
                {needsTap ? "TAP TO START" : "TAP TO ENTER"}
              </motion.span>
              <span className="text-[10px] tracking-[0.35em] uppercase text-[#FDFBF7]/60">
                Predict · Choose · Reveal
              </span>
            </motion.div>

            {/* Corner "INSERT COIN" */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="absolute top-4 left-4 flex items-center gap-2 z-10 pointer-events-none"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF]" style={{ boxShadow: "0 0 8px #00E5FF" }} />
              <span className="text-[9px] tracking-[0.35em] uppercase text-[#00E5FF] font-bold">insert coin</span>
            </motion.div>
          </button>

          {/* Mute toggle (above the tap-target so it can be clicked independently) */}
          <button
            data-testid="splash-mute-btn"
            onClick={(e) => {
              e.stopPropagation();
              setMuted((m) => !m);
            }}
            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full backdrop-blur-md bg-black/50 border border-white/20 text-[#FDFBF7] flex items-center justify-center hover:bg-black/70 hover:border-[#FF107A] transition-colors"
            aria-label={muted ? "Unmute intro sound" : "Mute intro sound"}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
